import { Divider, Grid } from '@mui/material';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { IRecordField, IRecordFieldsList } from 'ui-component/records/types';
import { FieldToView, RenderViewField } from './RenderViewField';
import { PanelLoader } from 'ui-component/RecordView/components/PanelLoader';
import { formatLabel, pinWay } from '../utils';
import { useContext } from 'react';
import { PanelFormsContext } from 'contexts/panelFormsContext';
import { findFieldByFieldName } from 'ui-component/RecordView/utils';
import { NON_EDITABLE_FIELDS } from 'ui-component/records/utils/editableFields';

export interface ViewPanelProps {
    fields: Record<keyof IRecordFieldsList, FieldToView>;
    handleOpenViewObjectPanel: (field: IRecordField, fieldName: string, editable: boolean) => void;
    headerData: IRecordFieldsList | null;
    doubleClickAction?: () => void;
    loading?: boolean;
    showFieldMenu?: boolean;
    rightHandPanelWidth?: number;
    isRecordViewer?: boolean;
}

const ViewPanel = ({
    fields,
    loading,
    handleOpenViewObjectPanel,
    headerData,
    doubleClickAction,
    showFieldMenu,
    rightHandPanelWidth,
    isRecordViewer
}: ViewPanelProps) => {
    const { setCustomFieldOrder: onReorderFields, enableDragAndDrop, pinnedFields, setSort } = useContext(PanelFormsContext);

    if (loading) {
        return <PanelLoader />;
    }

    const fieldEntries = Object.entries(fields || {}).filter(([key]) => !(NON_EDITABLE_FIELDS as ReadonlyArray<string>).includes(key));

    const pinnedUpFieldEntries = pinnedFields
        .filter((fieldName) => fieldName.includes(pinWay.up))
        .map((fieldName) => findFieldByFieldName(fieldName.replace(pinWay.up, ''), fieldEntries))
        .filter(Boolean) as [string, any][];

    const pinnedDownFieldEntries = pinnedFields
        .filter((fieldName) => fieldName.includes(pinWay.down))
        .map((fieldName) => findFieldByFieldName(fieldName.replace(pinWay.down, ''), fieldEntries))
        .filter(Boolean) as [string, any][];

    const unpinnedFieldEntries = fieldEntries.filter(([label, data]) => {
        const fieldId = `${label}::${data.id}`;
        return !pinnedFields.some(
            (pinnedField) =>
                pinnedField.includes(fieldId) || pinnedField.includes(fieldId + pinWay.up) || pinnedField.includes(fieldId + pinWay.down)
        );
    });

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination || !onReorderFields) return;
        if (result.destination.index === result.source.index) return;
        setSort('none');

        const newUnpinnedOrder = unpinnedFieldEntries.map(([label, data], index) => {
            const fieldId = `${label}-${data.id}`;
            if (fieldId === result.draggableId) {
                return `${label}::${data.id}::${result.destination?.index}`;
            }

            if (index >= (result.destination?.index || 0)) {
                return `${label}::${data.id}::${index + 1}`;
            }

            return `${label}::${data.id}::${index}`;
        });

        const newCompleteOrder = [
            ...pinnedUpFieldEntries.map(([label, data]) => `${label}::${data.id}`),
            ...pinnedDownFieldEntries.map(([label, data]) => `${label}::${data.id}`),
            ...newUnpinnedOrder
        ];

        onReorderFields(newCompleteOrder);
    };

    if (!enableDragAndDrop) {
        return (
            <Grid
                container
                data-testid="details-list"
                onDoubleClick={doubleClickAction}
                sx={{
                    height: '100%',
                    flexDirection: 'column',
                    position: 'relative'
                }}
            >
                {pinnedUpFieldEntries.length > 0 && (
                    <Grid
                        item
                        sx={{
                            pl: '0.8745rem',
                            pr: '0.5rem',
                            width: '100%',
                            maxWidth: '100%',
                            position: 'sticky',
                            top: 0,
                            backgroundColor: 'background.paper',
                            zIndex: 1
                        }}
                    >
                        {pinnedUpFieldEntries.map(([label, data]) => (
                            <RenderViewField
                                field={{ ...data, label, customLabel: formatLabel(label) }}
                                key={label}
                                headerData={headerData}
                                handleOpenViewObjectPanel={handleOpenViewObjectPanel}
                                showFieldMenu={showFieldMenu}
                                rightHandPanelWidth={rightHandPanelWidth}
                                isRecordViewer={isRecordViewer}
                            />
                        ))}
                        <Divider sx={{ my: '0.5rem', width: '100%' }} />
                    </Grid>
                )}
                <Grid
                    item
                    sx={{
                        pl: '0.8745rem',
                        pr: '0.5rem',
                        width: '100%',
                        maxWidth: '100%',
                        flex: 1,
                        overflowY: 'auto'
                    }}
                >
                    {unpinnedFieldEntries.map(([label, data]) => (
                        <RenderViewField
                            field={{ ...data, label, customLabel: formatLabel(label) }}
                            key={label}
                            headerData={headerData}
                            handleOpenViewObjectPanel={handleOpenViewObjectPanel}
                            showFieldMenu={showFieldMenu}
                            rightHandPanelWidth={rightHandPanelWidth}
                            isRecordViewer={isRecordViewer}
                        />
                    ))}
                </Grid>

                {pinnedDownFieldEntries.length > 0 && (
                    <Grid
                        item
                        sx={{
                            pl: '0.8745rem',
                            pr: '0.5rem',
                            width: '100%',
                            maxWidth: '100%',
                            position: 'sticky',
                            bottom: 0,
                            backgroundColor: 'background.paper',
                            zIndex: 1
                        }}
                    >
                        <Divider sx={{ my: '0.5rem', width: '100%' }} />
                        {pinnedDownFieldEntries.map(([label, data]) => (
                            <RenderViewField
                                field={{ ...data, label, customLabel: formatLabel(label) }}
                                key={`bottom-${label}`}
                                headerData={headerData}
                                handleOpenViewObjectPanel={handleOpenViewObjectPanel}
                                showFieldMenu={showFieldMenu}
                                rightHandPanelWidth={rightHandPanelWidth}
                                isRecordViewer={isRecordViewer}
                            />
                        ))}
                    </Grid>
                )}
            </Grid>
        );
    }

    return (
        <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="view-panel-fields">
                {(provided) => (
                    <Grid
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        container
                        data-testid="details-list"
                        onDoubleClick={doubleClickAction}
                        sx={{
                            height: '100%',
                            flexDirection: 'column',
                            position: 'relative'
                        }}
                    >
                        {pinnedUpFieldEntries.length > 0 && (
                            <Grid
                                item
                                sx={{
                                    pl: '0.8745rem',
                                    pr: '0.5rem',
                                    width: '100%',
                                    maxWidth: '100%',
                                    position: 'sticky',
                                    top: 0,
                                    backgroundColor: 'background.paper',
                                    zIndex: 1
                                }}
                            >
                                {pinnedUpFieldEntries.map(([label, data]) => (
                                    <RenderViewField
                                        field={{ ...data, label, customLabel: formatLabel(label) }}
                                        key={label}
                                        headerData={headerData}
                                        handleOpenViewObjectPanel={handleOpenViewObjectPanel}
                                        showFieldMenu={showFieldMenu}
                                        rightHandPanelWidth={rightHandPanelWidth}
                                        isRecordViewer={isRecordViewer}
                                    />
                                ))}
                                <Divider sx={{ my: '0.5rem', width: '100%' }} />
                            </Grid>
                        )}
                        <Grid
                            item
                            sx={{
                                pl: '0.8745rem',
                                pr: '0.5rem',
                                width: '100%',
                                maxWidth: '100%',
                                flex: 1,
                                overflowY: 'auto'
                            }}
                        >
                            {unpinnedFieldEntries.map(([label, data], index) => (
                                <Draggable key={`${label}-${data.id}`} draggableId={`${label}-${data.id}`} index={index}>
                                    {(dragProvided, snapshot) => (
                                        <div
                                            ref={dragProvided.innerRef}
                                            {...dragProvided.draggableProps}
                                            {...dragProvided.dragHandleProps}
                                            style={{
                                                ...dragProvided.draggableProps.style,
                                                opacity: 1,
                                                backgroundColor: '#FFF',
                                                transform: snapshot.isDragging ? dragProvided.draggableProps.style?.transform : 'none'
                                            }}
                                        >
                                            <RenderViewField
                                                field={{ ...data, label, customLabel: formatLabel(label) }}
                                                headerData={headerData}
                                                handleOpenViewObjectPanel={handleOpenViewObjectPanel}
                                                isDraggable
                                                isDragging={snapshot.isDragging}
                                                showFieldMenu={showFieldMenu}
                                                rightHandPanelWidth={rightHandPanelWidth}
                                                isRecordViewer={isRecordViewer}
                                            />
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </Grid>
                        {pinnedDownFieldEntries.length > 0 && (
                            <Grid
                                item
                                sx={{
                                    pl: '0.8745rem',
                                    pr: '0.5rem',
                                    width: '100%',
                                    maxWidth: '100%',
                                    position: 'sticky',
                                    bottom: 0,
                                    backgroundColor: 'background.paper',
                                    zIndex: 1
                                }}
                            >
                                <Divider sx={{ my: '0.5rem', width: '100%' }} />
                                {pinnedDownFieldEntries.map(([label, data]) => (
                                    <RenderViewField
                                        field={{ ...data, label, customLabel: formatLabel(label) }}
                                        key={`bottom-${label}`}
                                        headerData={headerData}
                                        handleOpenViewObjectPanel={handleOpenViewObjectPanel}
                                        showFieldMenu={showFieldMenu}
                                        rightHandPanelWidth={rightHandPanelWidth}
                                        isRecordViewer={isRecordViewer}
                                    />
                                ))}
                            </Grid>
                        )}
                    </Grid>
                )}
            </Droppable>
        </DragDropContext>
    );
};

export default ViewPanel;
